import type { Ref } from 'vue'
import type { UseFinancingData } from '~/store-v2/financings/useFinancing'
import type { TagStep } from '~/ui-blocks/Formalization/Formalization.types'
import { computed } from 'vue'
import { STATUSES_MAPPING } from '~/services-v2/financial-bff/financial-bff.consts'

/** Main financing statuses that end the journey (no further edits are allowed). */
export const TERMINAL_STATUSES = ['expired', 'reproved', 'canceled'] as const

export type TerminalFinancingStatus = typeof TERMINAL_STATUSES[number]

type SectionKey = 'customer_data' | 'biometry_and_contract' | 'equipment' | 'payment_model'

/** Pure check: true when the given main financing status is terminal. */
export function isTerminalFinancingStatus(status: string | null | undefined): status is TerminalFinancingStatus {
  return !!status && (TERMINAL_STATUSES as readonly string[]).includes(status)
}

/**
 * Reactive flags for a formalization step when the main financing status may be
 * terminal (expired / reproved / canceled).
 *
 * - `isTerminalFinancingStatus`: the main financing status is terminal.
 * - `isTerminalReadOnly`: same as above, applied to EVERY step (approved included);
 *   drives `disabled` states, handler guards and the read-only upload mode.
 * - `isStepApproved`: the step section status is `approved`.
 * - `isStepStarted`: the step section status is filled (any value).
 * - `isTerminalLocked`: terminal AND step not approved AND step not started; only
 *   steps that never started keep the padlock. Started steps open in read-only mode.
 * - `terminalTag`: terminal-status tag for every non-approved step (open or locked);
 *   `null` otherwise, so approved steps keep their own tag.
 */
export function useTerminalStatusLock(
  financing: Ref<UseFinancingData | undefined>,
  sectionKey: SectionKey,
  t: (key: string) => string,
) {
  const isTerminal = computed(() => isTerminalFinancingStatus(financing.value?.status))

  const stepStatus = computed(() => financing.value?.section_statuses?.[sectionKey]?.status)

  const isStepApproved = computed(() => stepStatus.value === 'approved')

  const isStepStarted = computed(() => !!stepStatus.value)

  const isTerminalReadOnly = computed(() => isTerminal.value)

  const isTerminalLocked = computed(() => {
    return isTerminal.value && !isStepApproved.value && !isStepStarted.value
  })

  const terminalTag = computed<TagStep | null>(() => {
    if (!isTerminal.value || isStepApproved.value)
      return null

    const status = financing.value?.status as string
    const mapping = STATUSES_MAPPING[status]

    if (!mapping)
      return null

    return {
      id: status,
      text: t(mapping.textKey),
      variant: 'fill',
      size: 'medium',
      type: mapping.color,
    } as TagStep
  })

  return {
    isTerminalFinancingStatus: isTerminal,
    isTerminalReadOnly,
    isStepApproved,
    isStepStarted,
    isTerminalLocked,
    terminalTag,
  }
}
